import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios'; // Import axios to intercept it

/**
 * Enhanced API Tracker Hook
 * Automatically intercepts all fetch and axios requests without modifying existing code
 * Stores successful API calls in localStorage and associates them with page routes
 */
const useApiTracker = () => {
  const location = useLocation();
  const currentPageRef = useRef(location.pathname);
  const originalFetchRef = useRef(null);
  const axiosSetupRef = useRef(null);

  // Send API log to the logging endpoint
  // List of APIs to always skip
  const skipApis = [
    'api/refine',
    '.json',
    'api-hub.ventureintelligence.com/private/log',
    'api-v2live.vionweb.com/api/refine/getLocation',
    'api-v2live.vionweb.com/api/refine/subFirmType',
    'api-v2live.vionweb.com/api/getRefineData',
    'api-hub.ventureintelligence.com/private/common/forex-rate/',
    'v2-api.ventureintelligence.com/private/common/forex-monthly/',
    'api-v2live.vionweb.com/api/refine/getCities',
    'api-v2live.vionweb.com/api/refine/sector',
    'api-v2live.vionweb.com/api/tearSheetLog',
    'api-v2live.vionweb.com/api/logSearchTerm',
    'v2-api-ts1.ventureintelligence.com/multi_search',
    'api-v2live.vionweb.com/broadcasting/auth',
    'api-v2live.vionweb.com/api/refine/dealType',
    'api-v2live.vionweb.com/api/refine/re/types',
    'api-v2live.vionweb.com/api/refine/subSectors',
    'api-v2live.vionweb.com/api/refine/exitstatus',
    'api-v2live.vionweb.com/api/notes/options',
    'api-v2live.vionweb.com/api/notes',
    'api-v2live.vionweb.com/api/alert/list',
    '/api/leadTrail',
    // SSO requests carry Google / Microsoft ID tokens and nonces; never forward them to the API log
    '/api/sso/'
  ];

  // Check if URL should be skipped
  const shouldSkipUrl = useCallback((url) => {
    return skipApis.some(api => url.includes(api));
  }, []);

  // Check if notifications API should be logged
  const shouldLogNotifications = useCallback((apiUrl, frontendPath) => {
    return apiUrl.includes('api-v2live.vionweb.com/api/notifications')
      ? frontendPath.includes('/notification')
      : true; // for other URLs, continue with normal logic
  }, []);

  const sendApiLog = useCallback(async (apiData) => {
    // Skip if URL is in exclusion list
    if (shouldSkipUrl(apiData.url)) {
      return;
    }

    // Check notifications API condition
    if (!shouldLogNotifications(apiData.url, apiData.page)) {
      return;
    }

    try {
      const userId = localStorage.getItem('user_id');
      const email = localStorage.getItem('LOGGED_IN_EMAIL');
      
      if (!userId || !email) {
        console.warn('User ID or email not found in localStorage');
        return;
      }

      // Extract only the path portion from the full URL
      const getUrlPath = (url) => {
        try {
          const urlObj = new URL(url);
          return urlObj.pathname + urlObj.search; // includes path and query parameters
        } catch (e) {
          return url; // fallback to full URL if parsing fails
        }
      };

      const logData = {
        user_id: userId,
        email: email,
        f_url: apiData.page, // frontend page URL
        b_url: getUrlPath(apiData.url), // only the path portion of the backend API URL
        method: apiData.method,
        status_code: apiData.response.status,
        payload: apiData.payload,
        response_time: apiData.duration / 1000, // Convert to seconds
        // created_at is handled by the backend
      };

      await axios.post('https://api-hub.ventureintelligence.com/private/log/', logData);
      // console.log('🎯 API Log sent:', logData);
    } catch (error) {
      console.error('Error sending API log:', error);
    }
  }, []);

  // No longer need to save to storage as we send directly to API
  const saveApiCall = useCallback((apiData) => {
    sendApiLog(apiData);
  }, [sendApiLog]);

  // Create API call data object
  const createApiCallData = useCallback((url, method, payload, response, duration) => {
    return {
      timestamp: new Date().toISOString(),
      url: url,
      method: method.toUpperCase(),
      payload: payload || null,
      response: {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers?.entries?.() || []),
        data: response.data || null
      },
      duration: duration,
      page: currentPageRef.current,
      success: response.status >= 200 && response.status < 300
    };
  }, []);

  // Intercept fetch requests
  const interceptFetch = useCallback(() => {
    if (originalFetchRef.current) return; // Already intercepted
    
    const originalFetch = window.fetch;
    originalFetchRef.current = originalFetch;
    // console.log('🎯 Setting up fetch interceptor');

    window.fetch = async function(...args) {
      const [url, options = {}] = args;
      const startTime = Date.now();
      const method = options.method || 'GET';
      let payload = null;

      // Skip tracking for Google Analytics and excluded APIs
      if (url.toString().includes('analytics.google.com') || 
          url.toString().includes('google-analytics.com') ||
          shouldSkipUrl(url.toString())) {
        return originalFetch.apply(window, args);
      }
      
      // console.log('🔥 Fetch request intercepted:', method, url);
      
      // Extract payload
      if (options.body) {
        try {
          payload = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
        } catch {
          payload = options.body.toString();
        }
      }
      
      try {
        const response = await originalFetch.apply(window, args);
        const duration = Date.now() - startTime;
        
        // console.log('✅ Fetch response intercepted:', response.status, url);
        
        // Only track successful responses
        if (response.ok) {
          // Clone response to read data without consuming the original
          const clonedResponse = response.clone();
          let responseData = null;
          
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              responseData = await clonedResponse.json();
            } else {
              responseData = await clonedResponse.text();
            }
          } catch {
            responseData = null;
          }
          
          const apiData = createApiCallData(
            url, 
            method, 
            payload, 
            {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers,
              data: responseData
            }, 
            duration
          );
          
          saveApiCall(apiData);
        } else {
          console.log('⚠️ Fetch response not ok:', response.status, url);
        }
        
        return response;
      } catch (error) {
        console.error('❌ Fetch error:', error, url);
        throw error;
      }
    };
    
    // console.log('✅ Fetch interceptor successfully set up');
  }, [createApiCallData, saveApiCall]);

  // Intercept axios requests using the imported axios instance
  const interceptAxios = useCallback(() => {
    if (axiosSetupRef.current) return; // Already set up
    
    // console.log('🎯 Setting up axios interceptors');
    
    try {
      // Request interceptor
      const requestInterceptor = axios.interceptors.request.use(
        (config) => {
          config.metadata = { startTime: Date.now() };
          console.log('🔥 Axios request intercepted:', config.method?.toUpperCase(), config.url);
          return config;
        },
        (error) => {
          console.error('❌ Axios request error:', error);
          return Promise.reject(error);
        }
      );
      
      // Response interceptor
      const responseInterceptor = axios.interceptors.response.use(
        (response) => {
          const duration = Date.now() - (response.config.metadata?.startTime || 0);
          // console.log('✅ Axios response intercepted:', response.status, response.config.url);
          
          // Only track successful responses and check skip conditions
          if (response.status >= 200 && response.status < 300 && 
              !shouldSkipUrl(response.config.url) &&
              shouldLogNotifications(response.config.url, currentPageRef.current)) {
            const apiData = createApiCallData(
              response.config.url,
              response.config.method || 'GET',
              response.config.data,
              {
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                data: response.data
              },
              duration
            );
            
            sendApiLog(apiData);
          }
          
          return response;
        },
        (error) => {
          console.error('❌ Axios response error:', error);
          // Don't track failed requests as per requirement
          return Promise.reject(error);
        }
      );
      
      axiosSetupRef.current = { requestInterceptor, responseInterceptor };
      // console.log('✅ Axios interceptors successfully set up');
    } catch (error) {
      console.error('❌ Error setting up axios interceptors:', error);
    }
  }, [createApiCallData, saveApiCall]);

  // Handle page change
  const handlePageChange = useCallback(() => {
    const newPath = location.pathname;
    if (newPath !== currentPageRef.current) {
      // console.log('📍 Page changed from', currentPageRef.current, 'to', newPath);
      currentPageRef.current = newPath;
      
    }
  }, [location.pathname]);


  // Initialize everything
  useEffect(() => {
    // console.log('🚀 Initializing API Tracker');
    interceptFetch();
    interceptAxios();
    
    // Cleanup function
    return () => {
      // Restore original fetch
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current.bind(window);
        originalFetchRef.current = null;
        // console.log('🧹 Fetch interceptor cleaned up');
      }
      
      // Remove axios interceptors
      if (axiosSetupRef.current) {
        const { requestInterceptor, responseInterceptor } = axiosSetupRef.current;
        axios.interceptors.request.eject(requestInterceptor);
        axios.interceptors.response.eject(responseInterceptor);
        axiosSetupRef.current = null;
        // console.log('🧹 Axios interceptors cleaned up');
      }
    };
  }, [interceptFetch, interceptAxios]);

  // Handle page changes
  useEffect(() => {
    handlePageChange();
  }, [handlePageChange]);

  // Export data as JSON - now returns empty data since we're not storing anymore
  const exportApiData = useCallback(() => {
    return {
      currentSession: {
        sessionId: Date.now(),
        startTime: new Date().toISOString(),
        pages: {}
      }
    };
  }, []);

  // Get current API calls for this session - returns empty since we're not storing
  const getCurrentApiCalls = useCallback(() => {
    return {};
  }, []);

  // Get API calls for specific page - returns empty since we're not storing
  const getApiCallsForPage = useCallback((pagePath) => {
    return [];
  }, []);

  // Clear tracking data - no-op since we're not storing
  const clearTrackingData = useCallback(() => {
    // Nothing to clear
  }, []);

  return {
    getCurrentApiCalls,
    getApiCallsForPage,
    exportApiData,
    clearTrackingData,
    currentPage: currentPageRef.current
  };
};

export default useApiTracker;